import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks whether an `<img>` has actually painted, so callers can fade it in
 * rather than let it snap into place when it decodes.
 *
 * `complete` is the important part: an image served from cache is already
 * painted by the time it mounts, so it must NOT replay the fade. Without that
 * check every remount — a filter toggle, a reorder, a revisit — would flash the
 * whole grid again.
 */
export const useImageLoaded = (src?: string) => {
  const elementRef = useRef<HTMLImageElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  // `complete` is true for an image with no src at all, so pair it with
  // `currentSrc` — otherwise a missing image would report itself as painted.
  const syncFromElement = useCallback(() => {
    const element = elementRef.current;
    setLoaded(Boolean(element?.complete && element.currentSrc));
  }, []);

  const imageRef = useCallback(
    (element: HTMLImageElement | null) => {
      elementRef.current = element;
      syncFromElement();
    },
    [syncFromElement]
  );

  // Re-arm for an incoming src, unless that one is cached too.
  useEffect(syncFromElement, [src, syncFromElement]);

  const markLoaded = useCallback(() => {
    if (src) setLoaded(true);
  }, [src]);

  return { loaded, imageRef, markLoaded };
};

export default useImageLoaded;
